import { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Globe, Loader2 } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Reveal } from "@/components/landing/Reveal";
import { Star } from "@/components/landing/Logo";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full rounded-2xl border px-5 py-3.5 text-sm outline-none transition-colors duration-300 focus:border-[var(--camel)] bg-white";

export default function Contact() {
  const { t, lang } = useLang();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [feedback, setFeedback] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, { ...form, subject: form.subject || null, lang });
      setStatus("success");
      setFeedback(c.form.success);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      setStatus("error");
      setFeedback(err?.response?.status === 429 ? c.form.rateLimited : c.form.error);
    }
  };

  const infoRows = [
    { Icon: MapPin, label: c.addressLabel, value: `MYSYMP AG\nStudenstrasse 14B\nCH-6207 Nottwil, ${c.country}` },
    { Icon: Phone, label: c.phoneLabel, value: "+41 79 471 06 25", href: "tel:+41794710625" },
    { Icon: Mail, label: c.emailLabel, value: "info@mysymp.ch", href: "mailto:info@mysymp.ch" },
    { Icon: Globe, label: c.webLabel, value: "mira-mirror.ai · mysymp.ch" },
  ];

  return (
    <main style={{ background: "var(--surface)", color: "var(--on-surface)" }} className="min-h-screen">
      <Navbar />
      <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-36 pb-24" data-testid="contact-page">
        <Reveal>
          <p className="flex items-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--camel)", letterSpacing: "0.16em" }}>
            <Star size={11} /> {c.kicker}
          </p>
          <h1 className="mt-5 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight" data-testid="contact-title">
            {c.title}
          </h1>
          <p className="mt-5 max-w-xl text-base md:text-lg leading-relaxed" style={{ color: "var(--muted-warm)" }}>
            {c.sub}
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <div className="rounded-3xl border p-8 sm:p-10" style={{ background: "var(--surface-secondary)", borderColor: "var(--line)" }} data-testid="contact-info-card">
              <h2 className="font-serif-display text-2xl font-semibold">{c.infoTitle}</h2>
              <div className="mt-8 space-y-7">
                {infoRows.map(({ Icon, label, value, href }) => (
                  <div key={label} className="flex items-start gap-4" data-testid={`contact-info-${label.toLowerCase()}`}>
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl" style={{ background: "var(--rose-soft)" }}>
                      <Icon size={19} strokeWidth={1.6} style={{ color: "var(--camel)" }} />
                    </span>
                    <div>
                      <p className="text-xs font-medium uppercase" style={{ color: "var(--muted-warm)", letterSpacing: "0.12em" }}>{label}</p>
                      {href ? (
                        <a href={href} className="mt-1 block text-sm sm:text-base font-medium transition-colors duration-300 hover:text-[var(--camel)]">
                          {value}
                        </a>
                      ) : (
                        <p className="mt-1 whitespace-pre-line text-sm sm:text-base font-medium">{value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-9 border-t pt-6 text-sm" style={{ borderColor: "var(--line)", color: "var(--muted-warm)" }}>
                ✦ {c.hoursNote}
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <form
              onSubmit={submit}
              className="rounded-3xl border p-8 sm:p-10 shadow-sm"
              style={{ background: "var(--surface)", borderColor: "var(--line)" }}
              data-testid="contact-form"
            >
              <h2 className="font-serif-display text-2xl font-semibold">{c.form.title}</h2>
              <div className="mt-7 space-y-4">
                <input data-testid="contact-input-name" required minLength={2} className={inputCls} style={{ borderColor: "var(--line)" }} placeholder={c.form.name} value={form.name} onChange={set("name")} />
                <input data-testid="contact-input-email" required type="email" className={inputCls} style={{ borderColor: "var(--line)" }} placeholder={c.form.email} value={form.email} onChange={set("email")} />
                <input data-testid="contact-input-subject" className={inputCls} style={{ borderColor: "var(--line)" }} placeholder={c.form.subject} value={form.subject} onChange={set("subject")} />
                <textarea data-testid="contact-input-message" required minLength={5} rows={5} className={inputCls} style={{ borderColor: "var(--line)", resize: "none" }} placeholder={c.form.message} value={form.message} onChange={set("message")} />
              </div>
              <button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={status === "loading"}
                className="pill-btn mt-7 flex w-full items-center justify-center gap-2 rounded-full py-4 text-sm font-medium text-white disabled:opacity-60"
                style={{ background: "var(--camel)" }}
              >
                {status === "loading" && <Loader2 size={16} className="animate-spin" />}
                {status === "loading" ? c.form.sending : c.form.submit}
              </button>
              {feedback && (
                <p
                  data-testid="contact-form-feedback"
                  className="mt-5 rounded-2xl border px-5 py-3.5 text-sm text-center"
                  style={{
                    borderColor: status === "success" ? "var(--gold)" : "var(--rose)",
                    background: status === "success" ? "var(--surface-secondary)" : "var(--rose-soft)",
                  }}
                >
                  {feedback}
                </p>
              )}
            </form>
          </Reveal>
        </div>
      </div>
      <Footer />
    </main>
  );
}
